import {
  DOCUMENT,
  isPlatformBrowser,
  isPlatformServer,
  NgClass,
} from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import { environment } from '../../../../environments/environment';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ArtefactType } from '../../../constants/artefact-types';
import { routes } from '../../../constants/routes';
import { ImagePlaceholderDirective } from '../../../directives/image-placeholder.directive';
import { ImageLazyLoadingDirective } from '../../../directives/img-lazy-loading.directive';
import { SpotlightCategory } from '../../../models/category-types.model';
import { LoggedInUser } from '../../../models/logged-in-user.model';
import { ApiService } from '../../../services/api.service';
import { CachingService } from '../../../services/caching.service';
import { UtilityService } from '../../../services/utility.service';
import { UpvoteDownvoteComponent } from '../../utility/upvote-downvote/upvote-downvote.component';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { SectionDescriptionComponent } from '../section-description/section-description.component';
import { SectionHeadingComponent } from '../section-heading/section-heading.component';
import { ToasterService } from '../../../services/toaster.service';

@Component({
  selector: 'app-spotlight',
  standalone: true,
  imports: [
    SectionContainerComponent,
    SectionHeadingComponent,
    SectionDescriptionComponent,
    ImageLazyLoadingDirective,
    ImagePlaceholderDirective,
    NgClass,
    UpvoteDownvoteComponent,
  ],
  templateUrl: './spotlight.component.html',
  styleUrl: './spotlight.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SpotlightComponent implements OnInit {
  readonly artefactType = ArtefactType;
  public spotlightCategory = SpotlightCategory;
  public selectedCategory = this.spotlightCategory.article;
  articlesList: any = [];
  public description =
    'Stay updated with the latest AI news, exciting competitions, and upcoming hackathons—opportunities to learn, compete, and innovate in the world of AI.';
  filterParams: HttpParams;
  isLoading: boolean = false;
  private fetchArticleSub!: Subscription;
  userProfile: LoggedInUser['user'];
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private apiService: ApiService,
    public toaster: ToasterService,
    private cachingService: CachingService,
    private utilityService: UtilityService,
  ) {}

  ngOnInit(): void {
    this.fetchArticle();

    this.cachingService?.loggedInUser.subscribe((data) => {
      const user = data?.user;
      this.userProfile = user;
    });
  }

  showVoteBtn(id: any) {
    if (!this.userProfile) return true;
    return this.userProfile.id !== id;
  }

  fetchArticle(pageIndex = 1, noOfItems = 5) {
    this.isLoading = true;

    this.fetchArticleSub = this.apiService
      .fetchArticleAllListing(pageIndex, noOfItems, this.filterParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (resp: any) => {
          if (!resp.items.length) {
            this.articlesList = [];
            return;
          }

          this.articlesList = resp?.items ?? [];
          if (isPlatformBrowser(this.platformId)) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          }
        },
        error: (error) => {
          // this.isErrorFetchingToolkits = true;
          apiErrorHandler.call(this, error);
        },
      });
  }

  onSpotlightCategoryClick(category: SpotlightCategory) {
    this.selectedCategory = category;
    if (category === this.spotlightCategory.viewAll)
      this.router.navigate(['/', routes.home, routes.article, routes.all]);
  }

  handleVote(id: any) {
    this.articlesList = this.utilityService.toggleVoteById(
      id,
      this.articlesList,
    );
  }

  private initSwiperCarousel() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const swiperProps: SwiperOptions = {
      slidesPerView: 1.2,

      speed: 500,
      spaceBetween: 18,
      navigation: {
        prevEl: '.spotlight-carousel-arrow--prev',
        nextEl: '.spotlight-carousel-arrow--next',
      },
      loop: true,
      autoplay: {
        delay: 10000,
        stopOnLastSlide: false,
      },
      pagination: {
        el: '.spotlight-swiper-pagination ',
        clickable: true,
      },
      followFinger: true,
      breakpoints: {
        1300: {
          slidesPerView: 4,
          spaceBetween: 18,
        },
        1000: {
          slidesPerView: 3,
          spaceBetween: 18,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 18,
        },
      },
    };

    const swiperContainer: SwiperContainer = this.document.querySelector(
      '#spotlight-swiper-container',
    )!;
    Object.assign(swiperContainer, swiperProps);
    swiperContainer.initialize();
  }

  goToDetailsPage(id: string | number) {
    this.cachingService.setCache(`articlesList`, this.articlesList);
    this.router.navigate([
      '/',
      routes.home,
      routes.article,
      routes.details,
      id,
    ]);
  }

  handleSocialShare(articleListItem: any) {
    const url = `${environment.webUrl}${environment.baseHref}${routes.home}/${routes.article}/${routes.details}/${articleListItem?.id}`;
    this.utilityService.openSocialShareDialog(url);
  }
}
